import type { CellRange, Sheet } from './types'
import { normalizeRange } from './a1'

export function rangesIntersect(a: CellRange, b: CellRange) {
  return a.r1 <= b.r2 && a.r2 >= b.r1 && a.c1 <= b.c2 && a.c2 >= b.c1
}

export function mergeAt(sheet: Sheet, row: number, col: number): CellRange | undefined {
  return (sheet.merges ?? []).find((m) => row >= m.r1 && row <= m.r2 && col >= m.c1 && col <= m.c2)
}

export function expandForMerges(sheet: Sheet, range: CellRange): CellRange {
  let out = normalizeRange(range)
  let changed = true
  while (changed) {
    changed = false
    for (const m of sheet.merges ?? []) {
      if (!rangesIntersect(out, m)) continue
      const next = {
        r1: Math.min(out.r1, m.r1), c1: Math.min(out.c1, m.c1),
        r2: Math.max(out.r2, m.r2), c2: Math.max(out.c2, m.c2),
      }
      if (JSON.stringify(next) !== JSON.stringify(out)) { out = next; changed = true }
    }
  }
  return out
}

export function shiftMergesOnInsert(sheet: Sheet, axis: 'row' | 'col', at: number, count: number) {
  const a = axis === 'row' ? 'r1' : 'c1'
  const b = axis === 'row' ? 'r2' : 'c2'
  sheet.merges = (sheet.merges ?? []).map((m) => {
    const out = { ...m }
    if (at <= out[a]) { out[a] += count; out[b] += count }
    else if (at <= out[b]) out[b] += count
    return out
  })
}

export function shiftMergesOnDelete(sheet: Sheet, axis: 'row' | 'col', at: number, count: number) {
  const delEnd = at + count - 1
  const a = axis === 'row' ? 'r1' : 'c1'
  const b = axis === 'row' ? 'r2' : 'c2'
  sheet.merges = (sheet.merges ?? []).flatMap((m) => {
    const survivors: number[] = []
    for (let n = m[a]; n <= m[b]; n++) {
      if (n < at) survivors.push(n)
      else if (n > delEnd) survivors.push(n - count)
    }
    if (!survivors.length) return []
    const out = { ...m, [a]: Math.min(...survivors), [b]: Math.max(...survivors) }
    return out.r1 === out.r2 && out.c1 === out.c2 ? [] : [out]
  })
}
